import { useId, useState } from 'react';
import { EllipsisIcon } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  CheckList,
  ErrorNotice,
  Field,
  LoadingState,
  Modal,
  SearchInput,
  TextArea,
  TextInput,
  useDebouncedValue,
} from '@bcecloud/portal-ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import {
  CONTRACT_MATCH_REASON,
  approveInvoice,
  postingGroups,
  type ApproveResult,
  HELD_LINE_STATUSES,
  HOLD_ACTION,
  OPEN_LINE_STATUSES,
  describeEvent,
  editInvoiceLine,
  evenSplit,
  holdInvoiceLine,
  lineLabel,
  releaseInvoiceLine,
  searchContracts,
  searchVendors,
  setInvoiceVendor,
  splitInvoiceLine,
  type CandidateEvent,
  type HoldAction,
  type InvoiceLine,
  type VendorOption,
} from '@/lib/invoices';
import { formatMoney } from '@/proposals/lib/money';

/**
 * The review dialogs on an invoice (phase 3a): pick a line's contract or the
 * invoice's vendor, split a line, and set a line aside. Each calls the API and
 * hands back to the page, which refetches.
 */

/** Typed fewer characters than this, a search waits. */
const MIN_SEARCH = 2;
const MAX_SPLIT_PARTS = 20;

/** A list of choosable rows: the reviewer clicks one. */
function ChoiceList<T>({
  items,
  keyOf,
  render,
  onChoose,
  disabled,
}: {
  readonly items: readonly T[];
  readonly keyOf: (item: T) => string;
  readonly render: (item: T) => React.ReactNode;
  readonly onChoose: (item: T) => void;
  readonly disabled: boolean;
}): React.ReactElement {
  return (
    <ul className="divide-y divide-portal-line rounded-portal-card border border-portal-line">
      {items.map((item) => (
        <li key={keyOf(item)}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChoose(item)}
            className="w-full p-3 text-left text-portal-small hover:bg-portal-sky disabled:opacity-60"
          >
            {render(item)}
          </button>
        </li>
      ))}
    </ul>
  );
}

function EventRow({ event }: { readonly event: CandidateEvent }): React.ReactElement {
  return (
    <>
      <span className="font-bold">{event.contractNumber ?? 'No contract #'}</span>
      <span className="block text-portal-micro text-portal-muted">{describeEvent(event)}</span>
    </>
  );
}

/** "Which contract is this line for?": the matching candidates, or any event by search. */
export function ContractPicker({
  line,
  onClose,
  onSaved,
}: {
  readonly line: InvoiceLine;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): React.ReactElement {
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 250);
  const results = useQuery({
    queryKey: ['invoice-contract-options', query],
    queryFn: ({ signal }) => searchContracts(query, signal),
    enabled: query.length >= MIN_SEARCH,
  });
  const save = useMutation({
    mutationFn: (eventId: string | null) => editInvoiceLine(line.id, { eventId }),
    onSuccess: onSaved,
  });
  const candidates = [line.contractMatch?.event, ...(line.contractMatch?.candidates ?? [])].filter(
    (event): event is CandidateEvent => Boolean(event),
  );
  const shown = query.length >= MIN_SEARCH ? (results.data ?? []) : candidates;

  return (
    <Modal
      open
      title="Which contract is this line for?"
      subtitle={`${line.description} · ${formatMoney(line.amount)}`}
      onClose={onClose}
      actions={
        <>
          {line.assignment ? (
            <Button variant="ghost" onClick={() => save.mutate(null)} disabled={save.isPending}>
              Clear the contract
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <SearchInput
          label="Search contracts"
          placeholder="Contract #, client or opportunity"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <p className="text-portal-micro text-portal-muted">
          {query.length >= MIN_SEARCH
            ? 'Search results'
            : line.contractMatch?.reason === CONTRACT_MATCH_REASON.contractNotFound
              ? `The invoice says ${line.contractNumber ?? 'a contract'}, which isn't in BCE Cloud. Search for it, or pick from events around that date:`
              : candidates.length > 0
                ? 'Suggested from the invoice'
                : 'Search for the contract'}
        </p>
        {results.isFetching ? <LoadingState label="Searching" minimal /> : null}
        {save.isError ? <ErrorNotice inline message={errorMessage(save.error)} /> : null}
        {shown.length > 0 ? (
          <ChoiceList
            items={shown}
            keyOf={(event) => event.eventId}
            render={(event) => <EventRow event={event} />}
            onChoose={(event) => save.mutate(event.eventId)}
            disabled={save.isPending}
          />
        ) : query.length >= MIN_SEARCH && !results.isFetching ? (
          <p className="text-portal-small text-portal-muted">No contract matches “{query}”.</p>
        ) : null}
      </div>
    </Modal>
  );
}

/** Which Salesforce vendor sent the invoice. */
export function VendorPicker({
  documentId,
  invoiceVendorName,
  hasVendor,
  onClose,
  onSaved,
}: {
  readonly documentId: string;
  readonly invoiceVendorName: string | null;
  readonly hasVendor: boolean;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): React.ReactElement {
  const [search, setSearch] = useState(invoiceVendorName ?? '');
  const query = useDebouncedValue(search.trim(), 250);
  const results = useQuery({
    queryKey: ['invoice-vendor-options', query],
    queryFn: ({ signal }) => searchVendors(query, signal),
    enabled: query.length >= MIN_SEARCH,
  });
  const save = useMutation({
    mutationFn: (vendor: VendorOption | null) => setInvoiceVendor(documentId, vendor),
    onSuccess: onSaved,
  });
  const shown = results.data ?? [];

  return (
    <Modal
      open
      title="Which Salesforce vendor sent this?"
      subtitle="Posted lines name this vendor."
      onClose={onClose}
      actions={
        <>
          {hasVendor ? (
            <Button variant="ghost" onClick={() => save.mutate(null)} disabled={save.isPending}>
              Clear the vendor
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <SearchInput label="Search vendors" placeholder="Vendor name" value={search} onChange={(event) => setSearch(event.target.value)} />
        {results.isFetching ? <LoadingState label="Searching" minimal /> : null}
        {save.isError ? <ErrorNotice inline message={errorMessage(save.error)} /> : null}
        {shown.length > 0 ? (
          <ChoiceList
            items={shown}
            keyOf={(vendor) => vendor.accountId}
            render={(vendor) => vendor.name}
            onChoose={(vendor) => save.mutate(vendor)}
            disabled={save.isPending}
          />
        ) : query.length >= MIN_SEARCH && !results.isFetching ? (
          <p className="text-portal-small text-portal-muted">No Salesforce account matches “{query}”.</p>
        ) : null}
      </div>
    </Modal>
  );
}

/** Split a line into parts that add up to it; each part keeps the contract and category, which can then be changed. */
export function SplitDialog({
  line,
  onClose,
  onSaved,
}: {
  readonly line: InvoiceLine;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): React.ReactElement {
  const formId = useId();
  const [amounts, setAmounts] = useState<string[]>(() => evenSplit(line.amount, 2).map((amount) => amount.toFixed(2)));
  const parsed = amounts.map((value) => Number(value));
  const sumCents = parsed.reduce((total, amount) => total + Math.round((Number.isFinite(amount) ? amount : 0) * 100), 0);
  const remaining = (Math.round(line.amount * 100) - sumCents) / 100;
  const valid = amounts.length >= 2 && parsed.every((amount) => Number.isFinite(amount) && amount !== 0) && remaining === 0;
  const save = useMutation({
    mutationFn: () => splitInvoiceLine(line.id, parsed.map((amount) => ({ amount }))),
    onSuccess: onSaved,
  });

  return (
    <Modal
      open
      title={`Split line ${lineLabel(line)}`}
      subtitle={`${line.description} · ${formatMoney(line.amount)}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={formId} disabled={!valid || save.isPending}>
            {save.isPending ? 'Splitting…' : `Split into ${amounts.length}`}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) save.mutate();
        }}
      >
        {amounts.map((value, index) => (
          <Field key={index} label={`Part ${index + 1}`} htmlFor={`${formId}-${index}`}>
            <div className="flex items-center gap-2">
              <TextInput
                id={`${formId}-${index}`}
                inputMode="decimal"
                value={value}
                onChange={(event) => setAmounts(amounts.map((current, i) => (i === index ? event.target.value : current)))}
              />
              {amounts.length > 2 ? (
                <Button variant="ghost" onClick={() => setAmounts(amounts.filter((_, i) => i !== index))}>
                  Remove
                </Button>
              ) : null}
            </div>
          </Field>
        ))}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => setAmounts([...amounts, '0.00'])}
            disabled={amounts.length >= MAX_SPLIT_PARTS}
          >
            Add a part
          </Button>
          <Button variant="ghost" onClick={() => setAmounts(evenSplit(line.amount, amounts.length).map((amount) => amount.toFixed(2)))}>
            Split evenly
          </Button>
        </div>
        <p className={remaining === 0 ? 'text-portal-small text-portal-muted' : 'text-portal-small text-rose-700'} aria-live="polite">
          {remaining === 0 ? 'The parts add up to the line.' : `${formatMoney(Math.abs(remaining))} ${remaining > 0 ? 'left to assign' : 'over the line amount'}.`}
        </p>
        <p className="text-portal-micro text-portal-muted">Each part keeps this line’s contract and category. Change them on the parts after splitting.</p>
        {save.isError ? <ErrorNotice inline message={errorMessage(save.error)} /> : null}
      </form>
    </Modal>
  );
}

const HOLD_COPY: Readonly<Record<HoldAction, { title: string; confirm: string; hint: string }>> = {
  [HOLD_ACTION.exclude]: {
    title: 'Exclude this line',
    confirm: 'Exclude',
    hint: 'An excluded line is never posted. You can bring it back later.',
  },
  [HOLD_ACTION.quarantine]: {
    title: 'Hold this line for later',
    confirm: 'Hold',
    hint: 'A held line waits in Held lines until you release it, and the rest of the invoice can post without it.',
  },
};

/** Exclude or hold a line, with an optional reason. */
export function HoldDialog({
  line,
  action,
  onClose,
  onSaved,
}: {
  readonly line: InvoiceLine;
  readonly action: HoldAction;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): React.ReactElement {
  const formId = useId();
  const [reason, setReason] = useState('');
  const save = useMutation({ mutationFn: () => holdInvoiceLine(line.id, action, reason.trim()), onSuccess: onSaved });
  const copy = HOLD_COPY[action];
  return (
    <Modal
      open
      title={copy.title}
      subtitle={`${line.description} · ${formatMoney(line.amount)}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={formId} disabled={save.isPending}>
            {copy.confirm}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Reason" htmlFor={`${formId}-reason`} hint={copy.hint}>
          <TextArea id={`${formId}-reason`} value={reason} onChange={(event) => setReason(event.target.value)} rows={3} />
        </Field>
        {save.isError ? <ErrorNotice inline message={errorMessage(save.error)} /> : null}
      </form>
    </Modal>
  );
}

/** What a row's menu can open. */
export type LineDialog =
  | { readonly kind: 'contract'; readonly line: InvoiceLine }
  | { readonly kind: 'split'; readonly line: InvoiceLine }
  | { readonly kind: 'hold'; readonly line: InvoiceLine; readonly action: HoldAction };

/** A line's actions: open lines can be split or set aside; held lines released. */
export function LineActions({
  line,
  onOpen,
  onReleased,
}: {
  readonly line: InvoiceLine;
  readonly onOpen: (dialog: LineDialog) => void;
  readonly onReleased: () => void;
}): React.ReactElement | null {
  const release = useMutation({ mutationFn: () => releaseInvoiceLine(line.id), onSuccess: onReleased });
  const open = OPEN_LINE_STATUSES.includes(line.status);
  const held = HELD_LINE_STATUSES.includes(line.status);
  if (!open && !held) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Actions for line ${lineLabel(line)}`}
          className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"
        >
          <EllipsisIcon className="size-4" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {open ? (
          <>
            <DropdownMenuItem onSelect={() => onOpen({ kind: 'contract', line })}>Change contract</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onOpen({ kind: 'split', line })}>Split</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onOpen({ kind: 'hold', line, action: HOLD_ACTION.quarantine })}>Hold for later</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onOpen({ kind: 'hold', line, action: HOLD_ACTION.exclude })}>Exclude</DropdownMenuItem>
          </>
        ) : (
          <DropdownMenuItem onSelect={() => release.mutate()}>Bring back to review</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const QBO_OPTION = 'qbo';

/**
 * "Post to Salesforce": what will post, contract by contract, and whether to
 * also make a QuickBooks Bill (off by default: a vendor billed through
 * Bill.com is already in QuickBooks).
 */
export function ApproveDialog({
  documentId,
  vendorName,
  lines,
  onClose,
  onDone,
}: {
  readonly documentId: string;
  readonly vendorName: string;
  readonly lines: readonly InvoiceLine[];
  readonly onClose: () => void;
  readonly onDone: (result: ApproveResult) => void;
}): React.ReactElement {
  const [createQboBill, setCreateQboBill] = useState(false);
  const groups = postingGroups(lines);
  const count = groups.reduce((sum, group) => sum + group.count, 0);
  const total = groups.reduce((sum, group) => sum + group.total, 0);
  const approve = useMutation({ mutationFn: () => approveInvoice(documentId, { createQboBill }), onSuccess: onDone });
  return (
    <Modal
      open
      title={`Post ${count} ${count === 1 ? 'line' : 'lines'} to Salesforce`}
      subtitle={`${formatMoney(total)} from ${vendorName}, onto ${groups.length} ${groups.length === 1 ? 'expense statement' : 'expense statements'}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={approve.isPending}>
            Cancel
          </Button>
          <Button onClick={() => approve.mutate()} disabled={approve.isPending || count === 0}>
            {approve.isPending ? 'Posting…' : 'Post to Salesforce'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ul className="divide-y divide-portal-line rounded-portal-card border border-portal-line">
          {groups.map((group) => (
            <li key={group.opportunityId} className="flex items-center justify-between gap-3 p-3 text-portal-small">
              <span>
                <span className="font-bold">{group.contractNumber ?? 'No contract #'}</span>
                <span className="block text-portal-micro text-portal-muted">{group.label}</span>
              </span>
              <span className="whitespace-nowrap text-right">
                {formatMoney(group.total)}
                <span className="block text-portal-micro text-portal-muted">
                  {group.count} {group.count === 1 ? 'line' : 'lines'}
                </span>
              </span>
            </li>
          ))}
        </ul>
        <p className="text-portal-micro text-portal-muted">
          Each line becomes an expense on its opportunity’s statement, and this invoice is attached to each opportunity.
          Lines still in review, held or excluded stay here.
        </p>
        <CheckList<typeof QBO_OPTION>
          label="QuickBooks"
          options={[
            {
              key: QBO_OPTION,
              label: 'Also create a QuickBooks Bill',
              detail: 'Leave this off for vendors who bill through Bill.com; their bills are already in QuickBooks.',
            },
          ]}
          values={createQboBill ? new Set([QBO_OPTION]) : new Set()}
          onToggle={(_, checked) => setCreateQboBill(checked)}
        />
        {approve.isError ? <ErrorNotice inline message={errorMessage(approve.error)} /> : null}
      </div>
    </Modal>
  );
}

/** What the last approval did, in a sentence or two. */
export function approveOutcome(result: ApproveResult): { readonly message: string; readonly problem: string | null } {
  const lines = `${result.posted} ${result.posted === 1 ? 'line' : 'lines'}`;
  const qbo = result.qbo === null ? '' : 'billId' in result.qbo ? ' A QuickBooks Bill was created.' : '';
  const message = `Posted ${lines} to Salesforce.${qbo}`;
  const problems = [
    result.failed.length > 0
      ? `${result.failed.length} ${result.failed.length === 1 ? 'line was' : 'lines were'} not posted: ${result.failed[0]?.error ?? ''}. ${result.failed.length === 1 ? 'It is' : 'They are'} back to Ready; post again to retry.`
      : null,
    result.qbo && 'error' in result.qbo ? `The QuickBooks Bill wasn't created: ${result.qbo.error}` : null,
  ].filter(Boolean);
  return { message, problem: problems.length > 0 ? problems.join(' ') : null };
}
